import { useEffect, useReducer, useRef, useState } from 'react';
import { isWorkosDobPending } from '../actions/workosLogin';
import useAuthGateState from '../hooks/useAuthGateState';
import useUnifiedLoginEnabled, {
  UNIFIED_LOGIN_DECISION_TIMEOUT_MS,
} from '../api/harness/useUnifiedLoginEnabled';
import { AUTH_TIMEOUT_MS } from './authConstants';
import { useResolvedToken } from './useToken';
import {
  didInboundExchangeFailThisLoad,
  didKickoffStandDownThisLoad,
  hasUnifiedLoginSession,
  markKickoffStoodDown,
  unifiedLoginRedirect,
} from './UnifiedLogin';

export type UnifiedLoginKickoffStatus = 'waiting' | 'legacy' | 'redirecting';

export type UnifiedLoginKickoff = {
  status: UnifiedLoginKickoffStatus;
  /** True while the login form must not render (flag wait or broker redirect). */
  holdsLogin: boolean;
};

/**
 * A marker set but no session means this tab already went to the broker and
 * came back without one. Redirecting again would bounce off a broker that
 * still has a session, so kickoff stands down and the legacy form renders.
 *
 * `login_required` keeps this marker so a blocked-cookie return cannot loop.
 */
const returnedFromBrokerWithoutSession = (loggedIn: boolean): boolean =>
  !loggedIn && hasUnifiedLoginSession();

const shouldSkipRedirect = (loggedIn: boolean, loggedOut: boolean): boolean =>
  loggedOut ||
  isWorkosDobPending() ||
  didInboundExchangeFailThisLoad() ||
  didKickoffStandDownThisLoad() ||
  returnedFromBrokerWithoutSession(loggedIn);

const kickoffResult = (
  status: UnifiedLoginKickoffStatus,
): UnifiedLoginKickoff => ({
  status,
  holdsLogin: status === 'waiting' || status === 'redirecting',
});

const useUnifiedLoginKickoff = (): UnifiedLoginKickoff => {
  const { kickoffReady, enabled, isReady, sessionMgmtEnabled } =
    useUnifiedLoginEnabled();
  const {
    candidateLogin: { checkedAuth, loggedIn },
    portalAuth: { loggedIn: portalLoggedIn, loggedOut },
  } = useAuthGateState();
  const resolvedToken = useResolvedToken();
  /**
   * Signed in by the same test the login pages use to send someone to reports
   * (`useAuthReady`'s `isLoggedIn || token`). Those pages still call this hook
   * on the render that navigates away, so kickoff must stand down for all of
   * them — a broker redirect finishing later would override that navigation.
   * The token is read without `useToken()`'s URL and storage side effects.
   * Deliberately applied for every caller (Privacy, WorkOS too): a visitor
   * already holding a session or report token is not sent to the broker.
   */
  const alreadyAuthenticated =
    Boolean(loggedIn) || portalLoggedIn || Boolean(resolvedToken);
  const [timedOut, setTimedOut] = useState(false);
  const [authCheckTimedOut, setAuthCheckTimedOut] = useState(false);

  /**
   * Set the instant this tab commits to the broker, before React re-renders.
   * A ref rather than state because the commit has to be visible to the very
   * next render: `unifiedLoginRedirect()` writes the session marker on its way
   * out, and from that render the marker otherwise reads as "returned from the
   * broker without a session" — which would drop the hold and flash the login
   * form while the browser is still navigating.
   */
  const redirectStarted = useRef(false);
  /**
   * Set once this instance's kickoff decision is made, either way. Never
   * re-opened. Instance-scoped, so a stand-down is also recorded through
   * `markKickoffStoodDown()`, which survives the remount.
   */
  const decided = useRef(false);
  /**
   * Bumped when the predicted hold below turns out to be wrong, to force the
   * one re-render that lets the form appear. Nothing else re-renders on that
   * path: the decision is kept in refs and the redirect never happened.
   */
  const [, forceHoldCorrection] = useReducer((tick: number) => tick + 1, 0);

  /**
   * Effective kickoff needs *both* treatments. Readiness stays independent —
   * each flag resolves on its own schedule — but sending someone to the broker
   * while inbound session management is off buys a round trip that can only
   * fall back to legacy. Kickoff-only therefore fails safe instead of bouncing.
   */
  const decisionReady = kickoffReady && isReady;
  const kickoffEnabled = enabled && sessionMgmtEnabled;

  useEffect(() => {
    if (decisionReady) return undefined;
    const id = globalThis.setTimeout(
      () => setTimedOut(true),
      UNIFIED_LOGIN_DECISION_TIMEOUT_MS,
    );
    return () => globalThis.clearTimeout(id);
  }, [decisionReady]);

  useEffect(() => {
    if (checkedAuth) return undefined;
    const id = globalThis.setTimeout(
      () => setAuthCheckTimedOut(true),
      AUTH_TIMEOUT_MS,
    );
    return () => globalThis.clearTimeout(id);
  }, [checkedAuth]);

  useEffect(() => {
    if (decided.current) return;

    // The auth-check timeout is terminal. Once fail-open has put the legacy
    // form on screen the candidate may already be typing an SSN, so a late
    // auth result must not redirect them away from it.
    if (authCheckTimedOut) {
      decided.current = true;
      markKickoffStoodDown();
      return;
    }

    if (!checkedAuth) return;

    if (shouldSkipRedirect(loggedIn, loggedOut) || alreadyAuthenticated) {
      decided.current = true;
      markKickoffStoodDown();
      return;
    }

    if (decisionReady && kickoffEnabled && !timedOut) {
      decided.current = true;
      // `unifiedLoginRedirect()` is async — the SDK chunk loads on demand — so
      // hold up front rather than on its answer. Waiting for the promise
      // before holding would render the login form for the frames in between,
      // and a truthiness test on the promise would hold forever when the
      // redirect never starts.
      redirectStarted.current = true;
      unifiedLoginRedirect().then(started => {
        if (started) return;
        // The broker never took over. Release the hold so the legacy form can
        // render; nothing else re-renders on this path.
        redirectStarted.current = false;
        markKickoffStoodDown();
        forceHoldCorrection();
      });
      return;
    }

    if (decisionReady || timedOut) {
      decided.current = true;
      markKickoffStoodDown();
    }
  }, [
    alreadyAuthenticated,
    authCheckTimedOut,
    checkedAuth,
    decisionReady,
    kickoffEnabled,
    loggedIn,
    loggedOut,
    timedOut,
  ]);

  // Navigation is under way; nothing may reopen the form.
  if (redirectStarted.current) return kickoffResult('redirecting');
  // A decision was already reached and it was not "redirect".
  if (decided.current) return kickoffResult('legacy');

  if (decisionReady && !kickoffEnabled) return kickoffResult('legacy');
  if (!checkedAuth) {
    return kickoffResult(authCheckTimedOut ? 'legacy' : 'waiting');
  }
  if (shouldSkipRedirect(loggedIn, loggedOut) || alreadyAuthenticated) {
    return kickoffResult('legacy');
  }

  // Everything the effect needs is already true, so it will redirect as soon
  // as this render commits. Hold here rather than rendering the form for one
  // frame first.
  if (decisionReady && kickoffEnabled && !timedOut) {
    return kickoffResult('redirecting');
  }

  if (!decisionReady && !timedOut) return kickoffResult('waiting');
  return kickoffResult('legacy');
};

export default useUnifiedLoginKickoff;
